import { useEffect, useRef, useState } from "react"
import { Reorder, useDragControls } from "framer-motion"
import { FaPlus } from "react-icons/fa"
import { MdEdit, MdDragIndicator } from "react-icons/md"
import { RiDeleteBin6Line } from "react-icons/ri"
import { BsChevronDown, BsPlayCircle } from "react-icons/bs"
import { FiCode } from "react-icons/fi"
import { useDispatch, useSelector } from "react-redux"

import {
  deleteSection,
  deleteSubSection,
  reorderSections,
  reorderSubSections,
} from "../../../../../services/operations/courseDetailsAPI"
import { setCourse } from "../../../../../slices/courseSlice"
import ConfirmationModal from "../../../../common/ConfirmationModal"
import SubSectionModal from "./SubSectionModal"

/* ─────────────────────────────────────────────────────────────── */
/* Lecture row — own drag controls so nested drag is independent   */
/* ─────────────────────────────────────────────────────────────── */
function LectureRow({ lecture, sectionIdx, lIdx, onView, onEdit, onDelete }) {
  const controls = useDragControls()

  return (
    <Reorder.Item
      as="div"
      value={lecture}
      dragListener={false}
      dragControls={controls}
      whileDrag={{ backgroundColor: "rgba(255,255,255,0.04)", zIndex: 10 }}
      className="flex items-center gap-2 border-b border-richblack-700/60 px-3 py-3 last:border-0 sm:px-5"
    >
      {/* Drag handle */}
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        className="flex-shrink-0 cursor-grab touch-none text-richblack-600 hover:text-richblack-400 active:cursor-grabbing"
        title="Drag to reorder"
      >
        <MdDragIndicator size={16} />
      </button>

      {lecture.type === "quiz"
        ? <FiCode size={13} className="flex-shrink-0 text-yellow-50/70" />
        : <BsPlayCircle size={13} className="flex-shrink-0 text-richblack-500" />
      }

      <button
        type="button"
        onClick={onView}
        className="min-w-0 flex-1 truncate text-left text-xs text-richblack-200 hover:text-richblack-5"
      >
        {sectionIdx + 1}.{lIdx + 1}&nbsp;{lecture.title}
      </button>

      <div className="flex flex-shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg p-2 text-richblack-500 transition-colors hover:bg-richblack-700 hover:text-richblack-5"
          title="Edit lecture"
        >
          <MdEdit size={15} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg p-2 text-richblack-500 transition-colors hover:bg-richblack-700 hover:text-pink-300"
          title="Delete lecture"
        >
          <RiDeleteBin6Line size={15} />
        </button>
      </div>
    </Reorder.Item>
  )
}

/* ─────────────────────────────────────────────────────────────── */
/* Section row — own drag controls + nested lecture reorder        */
/* ─────────────────────────────────────────────────────────────── */
function SectionRow({
  section,
  sectionIdx,
  isOpen,
  onToggle,
  onEditName,
  onDeleteSection,
  onAddLecture,
  onViewLecture,
  onEditLecture,
  onDeleteLecture,
  onSubSectionReorder,
}) {
  const controls = useDragControls()
  const [lectures, setLectures] = useState(section.subSection)
  const saveRef = useRef(null)

  // Keep local lectures in sync when Redux updates (after add/delete)
  useEffect(() => {
    setLectures(section.subSection)
  }, [section.subSection])

  const handleLectureReorder = (newOrder) => {
    setLectures(newOrder)
    clearTimeout(saveRef.current)
    saveRef.current = setTimeout(() => {
      onSubSectionReorder(section._id, newOrder.map((l) => l._id))
    }, 500)
  }

  return (
    <Reorder.Item
      as="div"
      value={section}
      dragListener={false}
      dragControls={controls}
      layout
      whileDrag={{
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        scale: 1.01,
        zIndex: 20,
      }}
      className="overflow-hidden rounded-xl border border-richblack-600 bg-richblack-700/60"
    >
      {/* Section header */}
      <div className="flex items-center gap-2 px-3 py-3 sm:px-4">
        {/* Drag handle */}
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="flex-shrink-0 cursor-grab touch-none text-richblack-600 hover:text-richblack-400 active:cursor-grabbing"
          title="Drag to reorder module"
        >
          <MdDragIndicator size={18} />
        </button>

        {/* Expand / name */}
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <BsChevronDown
            size={13}
            className={`flex-shrink-0 text-richblack-400 transition-transform duration-200 ${
              isOpen ? "rotate-0" : "-rotate-90"
            }`}
          />
          <span className="min-w-0 truncate text-sm font-semibold text-richblack-5">
            {sectionIdx + 1}.&nbsp;{section.sectionName}
          </span>
          <span className="ml-auto flex-shrink-0 rounded-full bg-richblack-600 px-2 py-0.5 text-[10px] text-richblack-300">
            {section.subSection.length}
          </span>
        </button>

        {/* Edit / delete buttons */}
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={onEditName}
            className="rounded-lg p-2 text-richblack-400 transition-colors hover:bg-richblack-600 hover:text-richblack-5"
            title="Edit section name"
          >
            <MdEdit size={16} />
          </button>
          <button
            type="button"
            onClick={onDeleteSection}
            className="rounded-lg p-2 text-richblack-400 transition-colors hover:bg-richblack-600 hover:text-pink-300"
            title="Delete section"
          >
            <RiDeleteBin6Line size={16} />
          </button>
        </div>
      </div>

      {/* Lectures — nested Reorder.Group */}
      {isOpen && (
        <div className="border-t border-richblack-600 bg-richblack-800/50">
          <Reorder.Group
            as="div"
            axis="y"
            values={lectures}
            onReorder={handleLectureReorder}
            className="select-none"
          >
            {lectures.map((lecture, lIdx) => (
              <LectureRow
                key={lecture._id}
                lecture={lecture}
                sectionIdx={sectionIdx}
                lIdx={lIdx}
                onView={() => onViewLecture(lecture)}
                onEdit={() => onEditLecture({ ...lecture, sectionId: section._id })}
                onDelete={() => onDeleteLecture(lecture._id, section._id)}
              />
            ))}
          </Reorder.Group>

          <div className="px-3 py-3 sm:px-5">
            <button
              type="button"
              onClick={onAddLecture}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-yellow-50 transition-colors hover:bg-richblack-700"
            >
              <FaPlus size={11} /> Add Lecture
            </button>
          </div>
        </div>
      )}
    </Reorder.Item>
  )
}

/* ─────────────────────────────────────────────────────────────── */
/* Main NestedView                                                 */
/* ─────────────────────────────────────────────────────────────── */
export default function NestedView({ handleChangeEditSectionName }) {
  const { course } = useSelector((state) => state.course)
  const { token } = useSelector((state) => state.auth)
  const dispatch = useDispatch()

  const [sections, setSections] = useState(course?.courseContent ?? [])
  const sectionSaveRef = useRef(null)

  const [addSubSection, setAddSubsection] = useState(null)
  const [viewSubSection, setViewSubSection] = useState(null)
  const [editSubSection, setEditSubSection] = useState(null)
  const [confirmationModal, setConfirmationModal] = useState(null)
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(course?.courseContent?.map((s) => [s._id, true]) ?? [])
  )

  // Keep sections in sync with Redux (after add/delete/create operations)
  useEffect(() => {
    setSections(course?.courseContent ?? [])
  }, [course])

  const toggleSection = (id) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))

  /* ── Reorder handlers ────────────────────── */

  const handleSectionsReorder = (newOrder) => {
    setSections(newOrder)
    clearTimeout(sectionSaveRef.current)
    sectionSaveRef.current = setTimeout(async () => {
      const result = await reorderSections(
        { courseId: course._id, orderedSectionIds: newOrder.map((s) => s._id) },
        token
      )
      if (result) dispatch(setCourse(result))
    }, 500)
  }

  const handleSubSectionReorder = async (sectionId, orderedSubSectionIds) => {
    const result = await reorderSubSections(
      { sectionId, courseId: course._id, orderedSubSectionIds },
      token
    )
    if (result) dispatch(setCourse(result))
  }

  /* ── Delete handlers ─────────────────────── */

  const handleDeleteSection = async (sectionId) => {
    const result = await deleteSection({ sectionId, courseId: course._id, token })
    if (result) dispatch(setCourse(result))
    setConfirmationModal(null)
  }

  const handleDeleteSubSection = async (subSectionId, sectionId) => {
    const result = await deleteSubSection({ subSectionId, sectionId, token })
    if (result) {
      const updated = course.courseContent.map((s) =>
        s._id === sectionId ? result : s
      )
      dispatch(setCourse({ ...course, courseContent: updated }))
    }
    setConfirmationModal(null)
  }

  return (
    <>
      <Reorder.Group
        as="div"
        axis="y"
        values={sections}
        onReorder={handleSectionsReorder}
        className="select-none space-y-3"
      >
        {sections.map((section, idx) => (
          <SectionRow
            key={section._id}
            section={section}
            sectionIdx={idx}
            isOpen={!!openSections[section._id]}
            onToggle={() => toggleSection(section._id)}
            onEditName={() =>
              handleChangeEditSectionName(section._id, section.sectionName)
            }
            onDeleteSection={() =>
              setConfirmationModal({
                text1: "Delete this Section?",
                text2: "All lectures in this section will be deleted.",
                btn1Text: "Delete",
                btn2Text: "Cancel",
                btn1Handler: () => handleDeleteSection(section._id),
                btn2Handler: () => setConfirmationModal(null),
              })
            }
            onAddLecture={() => setAddSubsection(section._id)}
            onViewLecture={setViewSubSection}
            onEditLecture={setEditSubSection}
            onDeleteLecture={(subSectionId, sectionId) =>
              setConfirmationModal({
                text1: "Delete this Lecture?",
                text2: "This lecture will be permanently deleted.",
                btn1Text: "Delete",
                btn2Text: "Cancel",
                btn1Handler: () => handleDeleteSubSection(subSectionId, sectionId),
                btn2Handler: () => setConfirmationModal(null),
              })
            }
            onSubSectionReorder={handleSubSectionReorder}
          />
        ))}
      </Reorder.Group>

      {addSubSection && (
        <SubSectionModal modalData={addSubSection} setModalData={setAddSubsection} add />
      )}
      {viewSubSection && (
        <SubSectionModal modalData={viewSubSection} setModalData={setViewSubSection} view />
      )}
      {editSubSection && (
        <SubSectionModal modalData={editSubSection} setModalData={setEditSubSection} edit />
      )}
      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </>
  )
}
